import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import {
  Badge,
  Button,
  Link,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import { FieldLabel } from 'src/components/Field';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faFileLines
} from '@fortawesome/pro-regular-svg-icons';
import { FormFieldSingleFileUpload } from 'src/components/FormField';
import { FormProvider, useForm } from 'react-hook-form';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import { fetchSubmissionContext } from 'src/api/queries';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { SidebarLayout } from 'src/layouts/SidebarLayout';
import { RevisionSidebar } from '../SubmissionUpload/RevisionSidebar';
import StepHeader from '../SubmissionUpload/components/StepHeader';
import {
  IRevisionRound,
  ISubmissionFile
} from '@publishing-engine/domain/models';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useQuery } from '@tanstack/react-query';
import {
  FormFieldInfoText,
  FormFieldManuscript,
  FormFieldTitlePage
} from '@publishing-engine/domain/types/article';
import SubmitResponseForm, {
  SubmitResponseHandle
} from 'src/components/Submission/MySubmission/components/AuthorResponse/SubmitResponseForm';
import RevisionRequest from './RevisionRequest';
import { useSubmitRevision } from 'src/api/commands';
import { getFileMetadata } from 'src/utils/file';
import { getRevisionUploadSectionLabel } from 'src/utils/revisionUploadSection';
import _ from 'lodash';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { generateValidationSchema } from 'src/utils/validation';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import { scrollToPendingFileField } from '../SubmissionUpload/fileReplacementNavigation';
interface Props {
  currentStep: number;
  totalSteps: number;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  goToStep: (step: number) => void;
  onClose: () => void;
  revisionRound: IRevisionRound | undefined;
}

const RevisionUploadStep: React.FC<Props> = ({
  currentStep,
  goToStep,
  totalSteps,
  onClose,
  revisionRound,
  goToNextStep
}) => {
  useEffect(() => scrollToPendingFileField('details'), []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getFieldState } = useSubmissionFields('submission');
  const configs = {
    ManuscriptType: getFieldState('ManuscriptType'),
    TitlePage: getFieldState('TitlePage'),
    Manuscript: getFieldState('Manuscript'),
    TitlePageSampleDownload: getFieldState('TitlePageSampleDownload'),
    ArticleType: getFieldState('ArticleType')
  };
  const revisionUploadSectionLabel = getRevisionUploadSectionLabel({
    hasManuscript: !!configs.Manuscript,
    hasTitlePage: !!configs.TitlePage
  });
  const {
    fieldsResponse: { fields }
  } = useSubmissionData();

  const TitlePageDetails = fields['TitlePage'];
  const ManuscriptDetails = fields['Manuscript'];

  const checkFormFieldValueIsUpdated = (previousValue: any, value: any) => {
    return (
      typeof previousValue !== 'undefined' &&
      !_.isEqual(getFileMetadata(value), getFileMetadata(previousValue!))
    );
  };

  const isTitlePageUpadted = checkFormFieldValueIsUpdated(
    TitlePageDetails?.previousValue as any,
    TitlePageDetails?.value as any
  );

  const isManuscriptUpadted = checkFormFieldValueIsUpdated(
    ManuscriptDetails?.previousValue as any,
    ManuscriptDetails?.value as any
  );

  const schema = generateValidationSchema({
    TitlePage: configs.TitlePage?.formFieldDefinition || null,
    Manuscript: configs.Manuscript?.formFieldDefinition || null
  });

  const methods = useForm<ISubmissionFormData>({
    defaultValues: {
      fileSet: {
        TitlePage: isTitlePageUpadted
          ? (configs.TitlePage?.value as ISubmissionFile)
          : {
              name: '',
              url: '',
              referenceName: '',
              extension: ''
            },
        Manuscript: isManuscriptUpadted
          ? (configs.Manuscript?.value as ISubmissionFile)
          : {
              name: '',
              url: '',
              referenceName: '',
              extension: ''
            }
      }
    },
    resolver: ajvResolver(schema)
  });
  // Compare against the values this upload step started with. `previousValue`
  // is the value from before the revision and can differ even when the author
  // makes no changes on this step.
  const initialFileSet = useRef(methods.getValues('fileSet'));

  const { watch } = methods;

  const childRef = useRef<SubmitResponseHandle | null>(null);

  const {
    workflowResponse: { submissionId, submissionContextId },
    organizationId,
    workflowResponse,
    refetchSubmission
  } = useSubmissionData();

  const { data: submissionContext } = useQuery({
    queryKey: ['submission-context', submissionContextId],
    queryFn: () =>
      submissionContextId ? fetchSubmissionContext(submissionContextId) : null
  });
  const toast = useToast();

  const articleTypes = configs.ArticleType?.value as string;

  const SubmitRevisionMutation = useSubmitRevision({
    submissionId,
    submissionContextId,
    organizationId,
    articleWorkflowId: workflowResponse.articleWorkflow.id ?? '',
    subtype: 'fields',
    invalidateSubmissionQueries: refetchSubmission
  });

  const manuscript = watch('fileSet.Manuscript');
  const titlePage = watch('fileSet.TitlePage');
  const hasUploadedFileChanged = (
    currentFile?: ISubmissionFile,
    initialFile?: ISubmissionFile
  ) => {
    const currentMetadata = currentFile?.url
      ? getFileMetadata(currentFile)
      : null;
    const initialMetadata = initialFile?.url
      ? getFileMetadata(initialFile)
      : null;
    return !_.isEqual(currentMetadata, initialMetadata);
  };
  const authorAttachments =
    childRef.current?.getValues()?.authorResponseAttachments;
  const existingFiles = {
    ...(manuscript?.name ? { Manuscript: manuscript } : {}),
    ...(configs?.TitlePage && titlePage?.name ? { TitlePage: titlePage } : {}),
    yourResponseAttachments: authorAttachments ?? []
  };

  const handleNextClick = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isChildValid = (await childRef.current?.triggerValidation()) ?? true;
    const isParentValid = await methods.trigger();
    if (!isParentValid || !isChildValid) {
      scrollToFirstError();
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async () => {
    try {
      if (childRef.current) {
        await childRef.current.submit();
      }
      const payload = {
        fileSet: {
          Manuscript: manuscript,
          ...(configs?.TitlePage && { TitlePage: titlePage })
        },
        isFileUpdated:
          hasUploadedFileChanged(
            manuscript,
            initialFileSet.current?.Manuscript as ISubmissionFile
          ) ||
          hasUploadedFileChanged(
            titlePage,
            initialFileSet.current?.TitlePage as ISubmissionFile
          )
      };
      const response = await SubmitRevisionMutation.mutateAsync(payload);
      if (response.isSuccess) {
        toast({
          variant: 'success',
          title: 'Response Saved',
          description: 'Title Page and Manuscript updated'
        });
        goToNextStep();
      }
    } catch (err) {
      return;
    }
  };
  if (configs.Manuscript?.formFieldDefinition?.params?.labels) {
    configs.Manuscript.formFieldDefinition.params.labels.main =
      'Revised Manuscript';
  }
  if (configs.TitlePage?.formFieldDefinition?.params?.labels) {
    configs.TitlePage.formFieldDefinition.params.labels.main =
      'Revised Title Page';
  }
  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleNextClick}>
          <Flex
            flexDirection="column"
            backgroundColor="surfaceSecondaryFill"
            minH="100vh"
          >
            <SidebarLayout
              sidebar={
                <RevisionSidebar
                  currentStep={currentStep}
                  onNavigate={step => {
                    goToStep(step);
                  }}
                />
              }
            >
              <StepHeader
                currentStep={currentStep + 1}
                totalSteps={totalSteps}
                heading="Revision upload & feedback response"
                description="Please upload your revised manuscript and title page below, and provide a detailed response addressing all feedback received from the publisher staff."
              />
              {revisionRound && (
                <RevisionRequest revisionRound={revisionRound} />
              )}

              <Flex gap={4} flexDirection="column">
                <Flex flexDirection="column" gap={2}>
                  {revisionUploadSectionLabel && (
                    <Text
                      textStyle="headingGroup"
                      id={revisionUploadSectionLabel}
                    >
                      {revisionUploadSectionLabel}
                    </Text>
                  )}
                  {submissionContext?.data.description && (
                    <Text textStyle="textBody">
                      {submissionContext?.data.description}{' '}
                      <Link
                        variant="secondary"
                        href={
                          submissionContext.config.reviewInformationLink ?? ''
                        }
                        target="_blank"
                      >
                        View journal guidelines
                      </Link>
                    </Text>
                  )}
                </Flex>

                {configs.ArticleType && (
                  <OutlineCard width="100%">
                    <Flex flexDirection="column" padding={4} gap={4}>
                      <FieldLabel
                        label={
                          configs.ArticleType.formFieldDefinition.params.labels
                            .main
                        }
                        subText={
                          configs.ArticleType.formFieldDefinition.params.labels
                            .subtitle
                        }
                        isRequired={
                          configs.ArticleType.formFieldDefinition.params
                            .validations.required
                        }
                        helpText={
                          configs.ArticleType.formFieldDefinition.params.labels
                            .helpText
                        }
                      />
                      <Box>
                        <Badge variant="attention" size="context">
                          <Flex gap={1} alignItems="center">
                            <FontAwesomeIcon icon={faFileLines} />
                            {articleTypes}
                          </Flex>
                        </Badge>
                      </Box>
                    </Flex>
                  </OutlineCard>
                )}

                {configs.Manuscript && (
                  <OutlineCard padding={4}>
                    <Flex flexDirection="column" gap={4}>
                      <FormFieldSingleFileUpload
                        submissionContextId={submissionContext?.id!}
                        config={
                          configs.Manuscript
                            .formFieldDefinition as FormFieldManuscript
                        }
                        methods={methods}
                        submissionId={submissionId}
                        width="50%"
                        submissionFields={existingFiles}
                      />
                    </Flex>
                  </OutlineCard>
                )}

                {configs.TitlePage && (
                  <OutlineCard padding={4}>
                    <Flex flexDirection="column" gap={4}>
                      <FormFieldSingleFileUpload
                        submissionContextId={submissionContext?.id!}
                        config={
                          configs.TitlePage
                            .formFieldDefinition as FormFieldTitlePage
                        }
                        methods={methods}
                        submissionId={submissionId}
                        width="50%"
                        TitlePageSampleDownload={
                          configs.TitlePageSampleDownload
                            ?.formFieldDefinition as FormFieldInfoText
                        }
                        submissionFields={existingFiles}
                      />
                    </Flex>
                  </OutlineCard>
                )}

                <Flex flexDirection="column" gap={2}>
                  <Flex flexDirection="column">
                    <Text textStyle="headingGroup" id="Your Response">
                      Your Response
                    </Text>
                    <Text textStyle="textBody">
                      Write a response for the review comments which will help
                      for the next round.
                    </Text>
                  </Flex>

                  {/* Child form (keeps its own useForm) — expose submit via ref */}
                  <SubmitResponseForm
                    ref={childRef}
                    submissionContextId={submissionContextId}
                    submissionId={submissionId}
                    manuscript={manuscript}
                    titlePage={titlePage}
                  />
                </Flex>
              </Flex>
            </SidebarLayout>
          </Flex>

          <Flex
            padding={4}
            bottom={0}
            zIndex={1}
            justifyContent="center"
            position="sticky"
            bgColor="surfacePrimaryFill"
            marginX="auto"
          >
            <Flex width="100%" justifyContent="space-between">
              {/* ----- Footer Buttons ----- */}
              <Button
                variant="caution"
                onClick={() => {
                  onClose();
                }}
              >
                Exit
              </Button>
              <Flex gap={4}>
                <Button
                  variant="primary"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Next
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </>
  );
};

export default RevisionUploadStep;
